import { useCallback, useEffect, useRef } from 'react';
import { useAppFetcher } from '~/hooks/use-app-fetcher';
import { useLatest } from 'ahooks';
import type { action } from '~/routes/remix-api.directory.set-featured-project';
import { SET_FEATURED_PROJECT } from '~/routes/remix-api.directory.set-featured-project';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useAppData } from '~/modules/root/hook/use-app-data';

type ErrorCallbackFn = (error: { __typename: string; Message: string }) => void;
type SuccessCallbackFn = (projectId: number, featured: boolean) => void;

/**
 * Hook to toggle the directory featured project status.
 * The function will be `undefined` if the current user does not have
 * the required admin permissions (IsStaffAdmin or IsHighAdmin).
 *
 * @param onError Callback function when an error occurs during the API call.
 * @param onSuccess Callback function when the toggle succeeds.
 */
export function useToggleFeaturedProject(
    onError?: ErrorCallbackFn,
    onSuccess?: SuccessCallbackFn,
) {
    const fetcher = useAppFetcher<{
        SetDirectoryFeaturedProject?: {
            __typename: string;
            Message?: string;
        };
    }>();
    const onErrorRef = useLatest<ErrorCallbackFn | undefined>(onError);
    const onSuccessRef = useLatest<SuccessCallbackFn | undefined>(onSuccess);
    const { user } = useAppData();

    const isStaffAdmin = user.__typename === 'Me' && user.IsStaffAdmin === true;
    const isHighAdmin =
        user.__typename === 'Me' &&
        'IsHighAdmin' in user &&
        user.IsHighAdmin === true;
    const canFeature = isStaffAdmin || isHighAdmin;

    // Capture the submitted feature value in a ref so it's available
    // when the fetcher is done (fetcher.formData is cleared when idle)
    const lastSubmittedFeatureRef = useRef<boolean>(false);

    const toggleFeaturedProject = useCallback(
        (projectID: number, feature: boolean) => {
            if (projectID <= 0) {
                return;
            }
            lastSubmittedFeatureRef.current = feature;
            fetcher.submit(
                {
                    projectID: projectID,
                    feature: feature,
                },
                {
                    method: 'post',
                    action: SET_FEATURED_PROJECT,
                },
            );
        },
        [fetcher],
    );

    useEffect(() => {
        if (!fetcherIsDone(fetcher)) {
            return;
        }

        const result = fetcher.data?.SetDirectoryFeaturedProject;
        if (
            (result?.__typename === 'UserError' ||
                result?.__typename === 'NotAuthorizedError') &&
            result.Message
        ) {
            onErrorRef.current?.({
                __typename: result.__typename,
                Message: result.Message,
            });
        } else if (
            result?.__typename === 'DirectoryFeaturedProject' &&
            'ProjectID' in result &&
            typeof result.ProjectID === 'number'
        ) {
            onSuccessRef.current?.(
                result.ProjectID,
                lastSubmittedFeatureRef.current,
            );
        }
    }, [fetcher, onErrorRef, onSuccessRef]);

    return {
        toggleFeaturedProject: canFeature ? toggleFeaturedProject : undefined,
    };
}
